/**
 * Authentication Hook - Multi-Provider Authentication
 *
 * Supports:
 * - Magic Link (Email Link) - passwordless sign-in via email
 * - Google Sign-In - one-click authentication with Google account
 *
 * Magic Link Flow:
 * 1. User enters email → sendMagicLink() → "Check your email" screen
 * 2. User clicks link → isEmailSignInLink() → completeEmailSignIn()
 * 3. User is now authenticated
 *
 * Google Sign-In Flow:
 * 1. User clicks "Continue with Google" → signInWithGoogle()
 * 2. Google popup/redirect → User selects account
 * 3. User is now authenticated
 */
import { useState, useEffect, useCallback } from 'react';
import { User } from 'firebase/auth';
import {
  onAuthChange,
  sendMagicLink,
  isEmailSignInLink,
  completeEmailSignIn,
  getStoredEmail,
  signOut as firebaseSignOut,
  signInWithGoogle as firebaseSignInWithGoogle,
  handleGoogleRedirectResult,
  getUserContext,
  updateUserContext,
  waitForAuthReady
} from '../services/firebase';

type AuthStep = 'idle' | 'email-sent' | 'completing' | 'needs-email' | 'google-pending';

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  step: AuthStep;
  sentToEmail: string | null;
}

interface AuthActions {
  sendLink: (email: string) => Promise<void>;
  completeSignIn: (email?: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  resetFlow: () => void;
  clearError: () => void;
}

export function useAuth(): AuthState & AuthActions {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
    step: 'idle',
    sentToEmail: null
  });

  // Check for email sign-in link and Google redirect on mount
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('[Auth] Initializing auth...');
      
      // IMPORTANT: Wait for Firebase Auth to be fully initialized with persistence
      // This ensures localStorage auth state is restored before we check
      await waitForAuthReady();
      console.log('[Auth] Firebase Auth ready');
      
      // Check for Google redirect result first
      try {
        console.log('[Auth] Checking for Google redirect result...');
        const googleUser = await handleGoogleRedirectResult();
        if (googleUser) {
          // User signed in via Google redirect
          console.log('[Auth] Google redirect success:', googleUser.email);
          setState(prev => ({
            ...prev,
            user: googleUser,
            loading: false,
            step: 'idle',
            error: null
          }));
          return;
        }
        console.log('[Auth] No Google redirect result found');
      } catch (error) {
        console.error('[Auth] Google redirect error:', error);
      }

      // Check for email sign-in link
      if (isEmailSignInLink()) {
        console.log('[Auth] Email sign-in link detected');
        const storedEmail = getStoredEmail();

        if (storedEmail) {
          // We have the email, complete sign-in automatically
          setState(prev => ({ ...prev, step: 'completing', loading: true }));
          try {
            await completeEmailSignIn(storedEmail);
            // Auth state change will handle the rest
          } catch (error) {
            setState(prev => ({
              ...prev,
              error: error instanceof Error ? error.message : 'Sign-in failed',
              step: 'idle',
              loading: false
            }));
          }
        } else {
          // No stored email - user may have opened link on different device
          setState(prev => ({ ...prev, step: 'needs-email', loading: false }));
        }
      }
    };

    initializeAuth();
  }, []);

  // Subscribe to auth state changes
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    
    const setupAuthListener = async () => {
      // Wait for auth to be initialized before listening
      await waitForAuthReady();
      console.log('[Auth] Setting up auth state listener...');
      
      unsubscribe = onAuthChange(async (user) => {
        console.log('[Auth] Auth state changed:', user ? user.email : 'null');
        setState(prev => ({
          ...prev,
          user,
          loading: false,
          step: user ? 'idle' : prev.step,
          error: null
        }));

        if (user) {
          // Load user context when they sign in
          try {
            const context = await getUserContext(user.uid);
            if (!context) {
              // Initialize context for new session
              await updateUserContext(user.uid, {
                lastIntent: null,
                lastAgent: null,
                messageCount: 0
              });
            }
          } catch (err) {
            console.error('[Auth] Error loading user context:', err);
          }
        }
      });
    };
    
    setupAuthListener();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Magic Link: Send email link
  const sendLink = useCallback(async (email: string) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      await sendMagicLink(email);
      setState(prev => ({
        ...prev,
        step: 'email-sent',
        sentToEmail: email,
        loading: false
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to send link',
        loading: false
      }));
    }
  }, []);

  // Magic Link: Complete sign-in from email link
  const completeSignIn = useCallback(async (email?: string) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      await completeEmailSignIn(email);
      // Auth state change will handle the rest
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Sign-in failed',
        step: 'idle',
        loading: false
      }));
    }
  }, []);

  // Google Sign-In
  const signInWithGoogle = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null, step: 'google-pending' }));
    try {
      const user = await firebaseSignInWithGoogle();
      // Explicitly set user on success (don't just rely on onAuthStateChanged)
      if (user) {
        console.log('[Auth] Google sign-in success:', user.email);
        setState(prev => ({
          ...prev,
          user,
          loading: false,
          step: 'idle',
          error: null
        }));
      }
    } catch (error) {
      // Check if it's a redirect (not an error)
      if (error instanceof Error && error.message.includes('Redirecting')) {
        // This is expected for redirect flow, don't show error
        console.log('[Auth] Redirecting to Google...');
        return;
      }
      
      console.error('[Auth] Google sign-in error:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Google sign-in failed',
        step: 'idle',
        loading: false
      }));
    }
  }, []);

  // Sign out
  const signOut = useCallback(async () => {
    try {
      await firebaseSignOut();
      setState(prev => ({ ...prev, step: 'idle', sentToEmail: null, error: null }));
    } catch (error) {
      console.error('Sign-out error:', error);
    }
  }, []);

  // Reset the auth flow (go back to initial state)
  const resetFlow = useCallback(() => {
    setState(prev => ({
      ...prev,
      step: 'idle',
      sentToEmail: null,
      error: null
    }));
  }, []);

  // Clear error message
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    sendLink,
    completeSignIn,
    signInWithGoogle,
    signOut,
    resetFlow,
    clearError
  };
}
